import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Settings, Check, ChevronRight, ShoppingCart, Info, Percent } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCurrency } from '../contexts/CurrencyContext';
import { calculateSalePrice } from '../utils/calculateSalePrice';
import { getRegionPrice } from '../utils/getRegionPrice';
import { useCart } from '../App';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Configurator = () => {
  const navigate = useNavigate();
  const { currency, formatPrice, formatMoney } = useCurrency();
  const { addItem } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [config, setConfig] = useState({
    size: null,
    vacuumPump: 'standard',
    accessories: []
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/products`);
      setProducts(res.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  const freezeDryers = products.filter(p => p.category === 'freeze-dryer');
  const accessories = products.filter(p => p.category === 'accessory');

  const selectedProduct = freezeDryers.find(p => config.size === p.id);
  
  const calculateTotal = () => {
    let total = 0;
    if (selectedProduct) {
      // Use sale price if available
      const priceInfo = calculateSalePrice(selectedProduct, currency);
      total += priceInfo.isOnSale && priceInfo.effectivePrice ? priceInfo.effectivePrice : getRegionPrice(selectedProduct, currency);
      // Premium pump upgrade
      if (config.vacuumPump === 'premium') {
        total += 500;
      }
    }
    // Add accessories
    config.accessories.forEach(accId => {
      const acc = accessories.find(a => a.id === accId);
      if (acc) total += getRegionPrice(acc, currency);
    });
    return total;
  };

  const handleAddToCart = () => {
    if (!selectedProduct) {
      toast.error('Please select a freeze dryer size');
      return;
    }
    
    // Add main product
    addItem({
      ...selectedProduct,
      configuration: {
        vacuumPump: config.vacuumPump
      }
    });
    
    // Add accessories
    config.accessories.forEach(accId => {
      const acc = accessories.find(a => a.id === accId);
      if (acc) addItem(acc);
    });
    
    toast.success('Configuration added to cart!');
    navigate('/cart');
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      
      {/* Hero */}
      <div className="pt-44 pb-12 bg-gradient-to-br from-[#003456] to-[#002B47]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="w-20 h-20 bg-[#CE8946] rounded-full flex items-center justify-center mx-auto mb-6">
            <Settings className="h-10 w-10 text-[#003456]" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Build My Freeze Dryer</h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto">
            Configure your perfect freeze drying system
          </p>
        </div>
      </div>
      
      <div className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin h-8 w-8 border-4 border-[#003456] border-t-transparent rounded-full mx-auto"></div>
            </div>
          ) : (
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Configuration Steps */}
              <div className="lg:col-span-2 space-y-8">
                {/* Step 1: Size */}
                <div className="bg-white rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center gap-3 mb-6">
                    <span className="w-8 h-8 bg-[#003456] text-white rounded-full flex items-center justify-center font-bold">1</span>
                    <h2 className="text-xl font-bold text-[#0a0203]">Choose Your Size</h2>
                  </div>
                  
                  <div className="grid md:grid-cols-3 gap-4">
                    {freezeDryers.map(product => (
                      <button
                        key={product.id}
                        onClick={() => setConfig({ ...config, size: product.id })}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                          config.size === product.id 
                            ? 'border-[#003456] bg-[#dbeef5]' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {config.size === product.id && (
                          <Check className="h-5 w-5 text-[#003456] float-right" />
                        )}
                        <h3 className="font-semibold text-[#0a0203]">{product.name}</h3>
                        <p className="text-sm text-gray-500 mb-2">{product.capacity}</p>
                        {(() => {
                          const priceInfo = calculateSalePrice(product, currency);
                          if (priceInfo.isOnSale && priceInfo.effectivePrice) {
                            return (
                              <div className="flex items-center gap-2">
                                <p className="text-lg font-bold text-[#003456]">{formatMoney(priceInfo.effectivePrice)}</p>
                                <span className="text-sm text-gray-400 line-through">{formatMoney(priceInfo.originalPrice)}</span>
                              </div>
                            );
                          }
                          return <p className="text-lg font-bold text-[#003456]">{formatMoney(getRegionPrice(product, currency))}</p>;
                        })()}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Step 2: Vacuum Pump */}
                <div className="bg-white rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center gap-3 mb-6">
                    <span className="w-8 h-8 bg-[#003456] text-white rounded-full flex items-center justify-center font-bold">2</span>
                    <h2 className="text-xl font-bold text-[#0a0203]">Select Vacuum Pump</h2>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <button
                      onClick={() => setConfig({ ...config, vacuumPump: 'standard' })}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        config.vacuumPump === 'standard' 
                          ? 'border-[#003456] bg-[#dbeef5]' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {config.vacuumPump === 'standard' && (
                        <Check className="h-5 w-5 text-[#003456] float-right" />
                      )}
                      <h3 className="font-semibold text-[#0a0203]">Standard Pump</h3>
                      <p className="text-sm text-gray-500 mb-2">Oil-based, reliable performance</p>
                      <p className="text-lg font-bold text-[#003456]">Included</p>
                    </button>
                    
                    <button
                      onClick={() => setConfig({ ...config, vacuumPump: 'premium' })}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        config.vacuumPump === 'premium' 
                          ? 'border-[#003456] bg-[#dbeef5]' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {config.vacuumPump === 'premium' && (
                        <Check className="h-5 w-5 text-[#003456] float-right" />
                      )}
                      <h3 className="font-semibold text-[#0a0203]">Premium Oil-Free Pump</h3>
                      <p className="text-sm text-gray-500 mb-2">No maintenance, quieter operation</p>
                      <p className="text-lg font-bold text-[#003456]">+{formatMoney(500)}</p>
                    </button>
                  </div>
                </div>

                {/* Step 3: Accessories */}
                <div className="bg-white rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center gap-3 mb-6">
                    <span className="w-8 h-8 bg-[#003456] text-white rounded-full flex items-center justify-center font-bold">3</span>
                    <h2 className="text-xl font-bold text-[#0a0203]">Add Accessories (Optional)</h2>
                  </div>
                  
                  {accessories.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No accessories available</p>
                  ) : (
                    <div className="grid md:grid-cols-2 gap-4">
                      {accessories.slice(0, 6).map(acc => (
                        <button
                          key={acc.id}
                          onClick={() => {
                            const newAccessories = config.accessories.includes(acc.id)
                              ? config.accessories.filter(id => id !== acc.id)
                              : [...config.accessories, acc.id];
                            setConfig({ ...config, accessories: newAccessories });
                          }}
                          className={`p-4 rounded-xl border-2 text-left transition-all ${
                            config.accessories.includes(acc.id) 
                              ? 'border-[#003456] bg-[#dbeef5]' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          {config.accessories.includes(acc.id) && (
                            <Check className="h-5 w-5 text-[#003456] float-right" />
                          )}
                          <h3 className="font-semibold text-[#0a0203]">{acc.name}</h3>
                          <p className="text-lg font-bold text-[#003456]">{formatMoney(getRegionPrice(acc, currency))}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Summary */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-2xl p-6 shadow-sm sticky top-36">
                  <h2 className="text-xl font-bold text-[#0a0203] mb-6">Your Configuration</h2>
                  
                  <div className="space-y-4 mb-6">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Freeze Dryer</span>
                      <span className="font-medium">
                        {selectedProduct ? selectedProduct.name : 'Not selected'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Vacuum Pump</span>
                      <span className="font-medium capitalize">{config.vacuumPump}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Accessories</span>
                      <span className="font-medium">{config.accessories.length} selected</span>
                    </div>
                  </div>
                  
                  <div className="border-t pt-4 mb-6">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold">Total</span>
                      <span className="text-2xl font-bold text-[#003456]">
                        {formatMoney(calculateTotal())}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      or as low as {formatMoney(Math.round(calculateTotal() / 24))}/mo
                    </p>
                  </div>
                  
                  <Button 
                    onClick={handleAddToCart}
                    className="w-full bg-[#CE8946] hover:bg-[#00a9b1] text-[#0a0203] font-semibold"
                    disabled={!config.size}
                  >
                    <ShoppingCart className="h-5 w-5 mr-2" />
                    Add to Cart
                  </Button>
                  
                  <div className="mt-4 p-3 bg-[#F8FAFC] rounded-lg flex items-start gap-2">
                    <Info className="h-4 w-4 text-[#003456] mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-gray-600">
                      Fast, insured shipping available. 30-day money-back guarantee.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default Configurator;
